import OnboardingRadioCardSelection, {
  OnboardingRadioCardSelectionProps,
} from 'features/onboarding/components/OnboardingRadioCardSelection/OnboardingRadioCardSelection';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ScrollView, StyleSheet, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import Button from 'shared/components/vxl/Button/Button';
import { IllustrationKeyType } from 'shared/components/vxl/Illustration/Illustration';
import FullScreenLoading from 'shared/components/vxl/loading/FullScreenLoading/FullScreenLoading';
import { NavigationBar } from 'shared/components/vxl/navigation/NavigationBar/NavigationBar';
import TitleXLText from 'shared/components/vxl/typography/primary/title/TitleXLText';
import { isAndroid } from 'shared/constants/platform';
import ScreenTransitionOverlay from 'shared/patterns/vxl/ScreenTransitionOverlay/ScreenTransitionOverlay';
import theme from 'shared/styles/theme';

interface OnboardingSelectionProps {
  optionsData: OnboardingRadioCardSelectionProps[];
  headlineText: string;
  onContinueButton: (
    selectedOption: string | undefined,
    didSkip?: boolean,
  ) => void;
  isLoading: boolean;
  skipButtonAnalyticsId: string;
  continueButtonAnalyticsId: string;
}

const OnboardingSelection = ({
  optionsData,
  headlineText,
  onContinueButton,
  isLoading,
  skipButtonAnalyticsId,
  continueButtonAnalyticsId,
}: OnboardingSelectionProps) => {
  const { bottom } = useSafeAreaInsets();

  const [selectedOption, setSelectedOption] = useState<string | undefined>(undefined);

  const { t: translatedText } = useTranslation('onboarding');

  const containerContentStyle = {
    paddingTop: 0,
  };

  return (
    <View style={styles.container}>
      <NavigationBar
        title=""
        trailingAccessories={
          <Button
            label={translatedText('shared.skip')}
            size="SMALL"
            type="TEXT"
            width="HUG"
            onPress={() => {
              onContinueButton(undefined, true);
            }}
            analyticsId={skipButtonAnalyticsId}
          />
        }
      />
      <View style={styles.container}>
        <ScrollView
          style={styles.scrollContainer}
          contentContainerStyle={containerContentStyle}
        >
          {headlineText ? (
            <TitleXLText style={styles.headlineText}>
              {headlineText}
            </TitleXLText>
          ) : null}
          <View style={styles.radioCardsContainer}>
            {optionsData.map((option) => (
              <OnboardingRadioCardSelection
                key={option.id}
                isSelected={option.id === selectedOption}
                labelText={option.labelText}
                illustration={option.illustration as IllustrationKeyType}
                tags={option.tags}
                id={option.id}
                onPress={() => {
                  setSelectedOption(option.id);
                }}
              />
            ))}
          </View>
        </ScrollView>
        <View
          style={[
            styles.footerContainer,
            {
              paddingBottom: isAndroid
                ? bottom + theme.spacing.m
                : bottom + theme.spacing.l,
            },
          ]}
        >
          <Button
            label={translatedText('onboardingOptionsScreen.continue')}
            onPress={() => {
              onContinueButton(selectedOption);
            }}
            isDisabled={!selectedOption}
            analyticsId={continueButtonAnalyticsId}
            customAttributes={{
              survey_option: optionsData.find(
                (option) => option.id === selectedOption,
              )?.analyticsSurveyOption,
            }}
          />
        </View>
      </View>
      <ScreenTransitionOverlay
        isLoading={isLoading}
        LoadingComponent={
          <FullScreenLoading
            isCoinLoader
            titleText={translatedText('loading.titleText')}
          />
        }
        ErrorComponent={null}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: theme.color.background.primary,
    flex: 1,
    flexDirection: 'column',
    flexGrow: 1,
    zIndex: 1,
  },
  footerContainer: {
    borderTopColor: theme.color.border.primary,
    borderTopWidth: 1,
    padding: theme.spacing.m,
  },
  headlineText: { padding: theme.spacing.m, textAlign: 'left' },
  radioCardsContainer: {
    backgroundColor: theme.color.background.primary,
    gap: theme.spacing.m,
    padding: theme.spacing.m,
  },
  scrollContainer: {
    flex: 1,
  },
});

export default OnboardingSelection;
